import React, { useContext, useEffect, useState } from "react";
import { CustomRegistrationQuestion } from "../types/RegistrationQuestion";
import { addCustomRegistrationQuestionsToEvent, addCustomRegistrationQuestionToEvent, deleteCustomRegistrationQuestion, getCustomRegistrationQuestionsForEvent, updateCustomRegistrationQuestion } from "../services/customRegistrationQuestionService";
import { useToast } from "../context/ToastContext";
import { EventContext } from "../context/EventContext";
import { useNavigate } from "react-router-dom";
import ToggleSwitch from "./ToggleSwitch";
import { ROUTES } from "../constants/routes";

export const EditCustomQuestions: React.FC = () => {
    const [customQuestions, setCustomQuestions] = useState<CustomRegistrationQuestion[]>([]);
    const [newQuestionText, setNewQuestionText] = useState("");
    const [editingId, setEditingId] = useState<string | null>(null);
    const [editingText, setEditingText] = useState("");
    const [editingRequired, setEditingRequired] = useState(false);
    const [newQuestionRequired, setNewQuestionRequired] = useState(false);

    const navigate = useNavigate();

    const { currentEvent } = useContext(EventContext);
    const { addToast } = useToast();

    useEffect(() => {
        loadCustomQuestions();
    }, [currentEvent]);

    const loadCustomQuestions = async () => {
        if (!currentEvent) {
            navigate(ROUTES.HOME);
            return;
        }
        const questions = await getCustomRegistrationQuestionsForEvent(currentEvent?.event_id);
        setCustomQuestions(questions);
    };

    const toggleNewQuestionRequired = () => {
        setNewQuestionRequired(!newQuestionRequired);
    }

    const handleAdd = async () => {
        if (!currentEvent || customQuestions.length >= 10) {
            return;
        }
        if (!newQuestionText.trim()) return;

        const customQuestion: CustomRegistrationQuestion = {
            event_id: currentEvent?.event_id,
            custom_registration_question_id: '',
            custom_question_text: newQuestionText,
            is_required: newQuestionRequired
        };
        const success = await addCustomRegistrationQuestionToEvent(currentEvent?.event_id, customQuestion);
        setNewQuestionText("");
        setNewQuestionRequired(false);
        loadCustomQuestions();

        if (success) {
            addToast(editingText + ' added to event.');
        } else {
            addToast('Error adding question to event, please try again;')
        }
    };

    const handleUpdate = async (id: string) => {
        if (!currentEvent) {
            return;
        }
        const customQuestion: CustomRegistrationQuestion = {
            event_id: currentEvent?.event_id,
            custom_registration_question_id: id,
            custom_question_text: editingText,
            is_required: editingRequired
        };

        const success = await updateCustomRegistrationQuestion(currentEvent.event_id, customQuestion);
        setEditingId(null);
        loadCustomQuestions();
        if (success) {
            addToast(editingText + ' updated.');
        } else {
            addToast('Error updating question for event, please try again;')
        }
    };

    const handleDelete = async (id: string) => {
        if (!currentEvent) {
            return;
        }
        if (!window.confirm("Delete this custom question?")) return;
        const success = await deleteCustomRegistrationQuestion(currentEvent.event_id, id);
        loadCustomQuestions();
        if (success) {
            addToast(editingText + ' Removed from event.');
        } else {
            addToast('Error removing question from event, please try again;')
        }
    };

    return (
        <div>
            <div style={{ marginBottom: 20 }}>
                <div style={{ display: 'flex' }}>
                    <input
                        value={newQuestionText}
                        onChange={(e) => setNewQuestionText(e.target.value)}
                        placeholder="New custom question"
                        style={{ maxWidth: '400px' }}
                        min={1}
                        max={500}
                    />
                    <ToggleSwitch label={newQuestionRequired ? 'Required' : 'Optional'} checked={newQuestionRequired} onChange={toggleNewQuestionRequired} />
                </div>
                <button
                    onClick={handleAdd}
                    disabled={customQuestions.length >= 10 || currentEvent?.is_locked}
                >
                    Add Custom Question ({10 - customQuestions.length} Available)
                </button>
            </div>

            {customQuestions.map((q) => (
                <div key={q.custom_registration_question_id} style={{ marginBottom: 12, gap: '5px' }}>
                    {editingId === q.custom_registration_question_id ? (
                        <div className='buttonsRow'>
                            <input
                                value={editingText}
                                onChange={(e) => setEditingText(e.target.value)}
                                style={{ maxWidth: '400px' }}
                            />
                            <div className='buttonsRight'>
                                <ToggleSwitch label={editingRequired ? 'Required' : 'Optional'} checked={editingRequired} onChange={() => { setEditingRequired(!editingRequired); }} />
                                <button onClick={() => handleUpdate(q.custom_registration_question_id)}>Save</button>
                                <button className='cancelButton' onClick={() => setEditingId(null)}>Cancel</button>
                            </div>
                        </div>
                    ) : (
                        <div className='buttonsRow'>
                            <span>{q.custom_question_text}</span>
                            <div className='buttonsRight'>
                                <ToggleSwitch label={q.is_required ? 'Required' : 'Optional'} disabled={true} checked={q.is_required} onChange={() => { }} />
                                <button onClick={() => {
                                    setEditingId(q.custom_registration_question_id);
                                    setEditingText(q.custom_question_text);
                                    setEditingRequired(q.is_required);
                                }}
                                disabled={currentEvent?.is_locked}
                                >Edit</button>
                                <button className='warningButton' onClick={() => handleDelete(q.custom_registration_question_id)}
                                    disabled={currentEvent?.is_locked}>Delete</button>
                            </div>
                        </div>
                    )}
                </div>
            ))}
        </div>
    );
};
